// @flow
import { all, fork, put, takeLatest, call } from 'redux-saga/effects';

import {
    getAllShipments as getAllShipmentsApi,
    getShipmentSurcharges as getShipmentSurchargesApi,
    getNonShipmentSurcharges as getNonShipmentSurchargesApi,
    getShipmentByExternalOrderId as getShipmentByExternalOrderIdApi,
    getOrder as getOrderApi,
    createManifest as createManifestApi,
    getAllShipmentsReport as getAllShipmentsReportApi,
    rateShipments as rateShipmentsApi,
} from '../../helpers/';

import { shipmentsApiResponseSuccess, shipmentsApiResponseError, getAllShipments as getAllShipmentsAction } from './actions';
import { ShipmentsActionTypes } from './constants';
import { shipmentResponseToShipmentObject } from '../../helpers/mappers/shipment';
import { shipmentSurchargeResponseToShipmentSurchargeObject } from '../../helpers/mappers/shipmentSurcharge';
import { nonShipmentSurchargeResponseToNonShipmentSurchargeObject } from '../../helpers/mappers/nonShipmentSurcharge';
import { manifestObjectToManifestRequest } from '../../helpers/mappers/manifest';
import { addToast } from '../actions';
import { paginationHeadersResponse } from '../../helpers/mappers/pagination';

function* getAllShipments({ payload: { query } }) {
    try {
        const getAllShipmentsResponse = yield call(getAllShipmentsApi, query);
        const shipments = getAllShipmentsResponse.data.map((shipmentResponse) =>
            shipmentResponseToShipmentObject(shipmentResponse)
        );
        const pagination = { ...paginationHeadersResponse(getAllShipmentsResponse.headers), ...query };
        yield put(shipmentsApiResponseSuccess(ShipmentsActionTypes.GET_ALL_SHIPMENTS, { shipments, pagination }));
    } catch (error) {
        yield put(addToast({ desc: error.errors[0].message, type: 'error' }));
        yield put(shipmentsApiResponseError(ShipmentsActionTypes.GET_ALL_SHIPMENTS, error));
    }
}

function* getShipmentSurcharges({ payload: { query } }) {
    try {
        const response = yield call(getShipmentSurchargesApi, query);
        const shipmentSurcharges = response.data.map((surchargeResponse) =>
            shipmentSurchargeResponseToShipmentSurchargeObject(surchargeResponse)
        );
        const pagination = { ...paginationHeadersResponse(response.headers), ...query };
        yield put(shipmentsApiResponseSuccess(ShipmentsActionTypes.GET_SHIPMENT_SURCHARGES, { shipmentSurcharges, pagination }));
    } catch (error) {
        yield put(addToast({ desc: error?.errors?.[0]?.message || 'Unable to load shipment surcharges', type: 'error' }));
        yield put(shipmentsApiResponseError(ShipmentsActionTypes.GET_SHIPMENT_SURCHARGES, error));
    }
}

function* getNonShipmentSurcharges({ payload: { query } }) {
    try {
        const response = yield call(getNonShipmentSurchargesApi, query);
        const nonShipmentSurcharges = response.data.map((surchargeResponse) =>
            nonShipmentSurchargeResponseToNonShipmentSurchargeObject(surchargeResponse)
        );
        const pagination = { ...paginationHeadersResponse(response.headers), ...query };
        yield put(shipmentsApiResponseSuccess(ShipmentsActionTypes.GET_NON_SHIPMENT_SURCHARGES, { nonShipmentSurcharges, pagination }));
    } catch (error) {
        yield put(addToast({ desc: error?.errors?.[0]?.message || 'Unable to load non-shipment surcharges', type: 'error' }));
        yield put(shipmentsApiResponseError(ShipmentsActionTypes.GET_NON_SHIPMENT_SURCHARGES, error));
    }
}

function* getShipmentByExternalOrderId({ payload: { externalOrderId } }) {
    try {
        const getShipmentByExternalOrderIdResponse = yield call(getShipmentByExternalOrderIdApi, externalOrderId);
        const shipments = getShipmentByExternalOrderIdResponse.data.map((shipmentResponse) =>
            shipmentResponseToShipmentObject(shipmentResponse)
        );
        console.log(shipments);
        yield put(shipmentsApiResponseSuccess(ShipmentsActionTypes.GET_SHIPMENT_BY_EXTERNAL_ORDER_ID, shipments));
    } catch (error) {
        yield put(shipmentsApiResponseError(ShipmentsActionTypes.GET_SHIPMENT_BY_EXTERNAL_ORDER_ID, error));
    }
}

function* shipmentsGetOrder({ payload: { orderId, returnLabel, fullResponse } }) {
    // try {
    //     const getOrderResponse = yield call(getOrderApi, orderId, returnLabel, fullResponse);
    //     const order = orderResponseToOrderObject(getOrderResponse.data);
    //     yield put(shipmentsApiResponseSuccess(ShipmentsActionTypes.GET_ORDER, order));
    // } catch (error) {
    //     yield put(addToast({ desc: error.errors[0].message, type: 'error' }));
    //     yield put(shipmentsApiResponseError(ShipmentsActionTypes.GET_ORDER, error));
    // }
}

function* shipmentsCreateManifest({ payload: { manifestSpec, returnImage, fullResponse } }) {
    try {
        const request = manifestObjectToManifestRequest(manifestSpec);
        const createManifestResponse = yield call(createManifestApi, request, returnImage, fullResponse);
        yield put(shipmentsApiResponseSuccess(ShipmentsActionTypes.CREATE_MANIFEST, createManifestResponse.data));
    } catch (error) {
        yield put(addToast({ desc: error.errors[0].message, type: 'error' }));
        yield put(shipmentsApiResponseError(ShipmentsActionTypes.CREATE_MANIFEST, error));
    }
}

function* getAllShipmentsReport({ payload: { query } }) {
    try {
        const getAllShipmentsReportResponse = yield call(getAllShipmentsReportApi, query);
        yield put(shipmentsApiResponseSuccess(ShipmentsActionTypes.GET_ALL_SHIPMENTS_REPORT, getAllShipmentsReportResponse.data));
    } catch (error) {
        yield put(shipmentsApiResponseError(ShipmentsActionTypes.GET_ALL_SHIPMENTS_REPORT, error));
    }
}

function* rateShipments({ payload: { query } }) {
    try {
        const response = yield call(rateShipmentsApi, query);
        const triggered = response?.data?.triggered_shipments ?? response?.data?.shipments_count;
        const asyncAccepted = response?.data?.status === 'accepted' && !!response?.data?.job_id;

        yield put(shipmentsApiResponseSuccess(ShipmentsActionTypes.RATE_SHIPMENTS, response.data));
        let desc = 'Shipment rating request accepted.';
        let type = 'success';
        if (typeof triggered === 'number') {
            desc =
                triggered === 0
                    ? 'No shipments matched the selected filters to rate.'
                    : `Rating triggered for ${triggered} shipment${triggered === 1 ? '' : 's'}.`;
            type = triggered === 0 ? 'info' : 'success';
        } else if (asyncAccepted) {
            desc = 'Shipment rating job queued and processing in the background.';
        }
        yield put(
            addToast({
                desc,
                type,
            })
        );

        if (query) {
            yield put(getAllShipmentsAction(query));
        }
    } catch (error) {
        const message = error?.errors?.[0]?.message || 'Unable to trigger shipment rating';
        yield put(addToast({ desc: message, type: 'error' }));
        yield put(shipmentsApiResponseError(ShipmentsActionTypes.RATE_SHIPMENTS, error));
    }
}

export function* watchGetAllShipments(): any {
    yield takeLatest(ShipmentsActionTypes.GET_ALL_SHIPMENTS, getAllShipments);
}

export function* watchGetShipmentSurcharges(): any {
    yield takeLatest(ShipmentsActionTypes.GET_SHIPMENT_SURCHARGES, getShipmentSurcharges);
}

export function* watchGetNonShipmentSurcharges(): any {
    yield takeLatest(ShipmentsActionTypes.GET_NON_SHIPMENT_SURCHARGES, getNonShipmentSurcharges);
}

export function* watchGetShipmentByExternalOrderId(): any {
    yield takeLatest(ShipmentsActionTypes.GET_SHIPMENT_BY_EXTERNAL_ORDER_ID, getShipmentByExternalOrderId);
}

export function* watchShipmentsGetOrder(): any {
    yield takeLatest(ShipmentsActionTypes.GET_ORDER, shipmentsGetOrder);
}

export function* watchShipmentsCreateManifest(): any {
    yield takeLatest(ShipmentsActionTypes.CREATE_MANIFEST, shipmentsCreateManifest);
}

export function* watchGetAllShipmentsReport(): any {
    yield takeLatest(ShipmentsActionTypes.GET_ALL_SHIPMENTS_REPORT, getAllShipmentsReport);
}

export function* watchRateShipments(): any {
    yield takeLatest(ShipmentsActionTypes.RATE_SHIPMENTS, rateShipments);
}

function* shipmentsLogSaga(): any {
    yield all([
        fork(watchGetAllShipments), 
        fork(watchGetShipmentSurcharges),
        fork(watchGetNonShipmentSurcharges),
        fork(watchGetShipmentByExternalOrderId),
        fork(watchShipmentsGetOrder), 
        fork(watchShipmentsCreateManifest),
        fork(watchGetAllShipmentsReport),
        fork(watchRateShipments)
    ]);
}

export default shipmentsLogSaga;
